import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function useWebhookSources() {
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get("/webhooks/sources");
      setSources(res.data?.sources || res.data || []);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refetch(); }, [refetch]);

  const createSource = useCallback(async (data) => {
    try {
      await api.post("/webhooks/sources", data);
      toast.success("Webhook source created");
      await refetch();
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create webhook source");
      return false;
    }
  }, [refetch]);

  const updateSource = useCallback(async (id, data) => {
    try {
      await api.put(`/webhooks/sources/${id}`, data);
      toast.success("Webhook source updated");
      await refetch();
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update webhook source");
      return false;
    }
  }, [refetch]);

  const deleteSource = useCallback(async (id) => {
    try {
      await api.delete(`/webhooks/sources/${id}`);
      toast.success("Webhook source deleted");
      await refetch();
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete webhook source");
      return false;
    }
  }, [refetch]);

  const rotateToken = useCallback(async (id) => {
    try {
      const res = await api.post(`/webhooks/sources/${id}/rotate`);
      toast.success("Token rotated");
      await refetch();
      return res.data?.token || null;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to rotate token");
      return null;
    }
  }, [refetch]);

  return {
    sources, loading, error, refetch,
    createSource, updateSource, deleteSource, rotateToken,
  };
}
